// client/src/components/pages/LexiStar/Replay/ReplayMultiMap.tsx
//
// ST-00277 — the Replay map with several trucks on one clock, on the shared LexiStarMap:
//  - every truck's track in its own colour (./replayMulti truckColor, the shared categorical
//    palette, one step per basemap) over a casing, as ONE line source; a truck's track can be
//    switched off (the list beside the map);
//  - every truck as the shared asset marker (map/assetMarker, as single-truck Replay and Live
//    View) in the status colour the organisation's display rules gave it at that moment
//    (./replayDisplay), with its registration on a tag in the truck's own colour under it;
//  - events as small rings (one circle layer: a 25-truck replay can carry thousands);
//  - the tenant's zones as they were at the replay clock (ST-00272, ./ReplayZones, unchanged);
//  - a click on a truck opens its card (ST-00270, map/AssetInfoCard) for that truck at the moment
//    on the clock; it follows the truck while the replay plays or is scrubbed; Esc, × or a click
//    on the map closes it. "Replay alone" in the card opens that truck's full replay.
// The camera fits every track on a new replay; Fit does it again.

import { memo, useCallback, useEffect, useId, useMemo, useRef, type ReactNode } from 'react';
import { Layer, Marker, Popup, Source } from 'react-map-gl/maplibre';
import type { LngLatBoundsLike, Map as MaplibreMap } from 'maplibre-gl';
import { Link } from 'react-router-dom';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { LexiStarMap } from '../../../../types/commonComponents';
import { AssetMarkerIcon, type AssetMarkerKind } from '../../../map/assetMarker';
import { ASSET_STATUS, ASSET_STATUS_CLASSES, type AssetStatusKey } from '../../../map/assetStatus';
import AssetInfoCard, { ASSET_INFO_POPUP_CLASS } from '../../../map/AssetInfoCard';
import DisplayMarks, { MarkGlyph, MarkerHalo } from '../../../map/DisplayMarks';
import { isHollowDisplay, type AssetDisplay } from '../../../map/displayRules';
import useReplayZones from './ReplayZones';
import { clockTime, stageKey } from './replayLogic';
import type { ReplayMapPosition } from './ReplayMap';
import type { ReplayDisplayLegend } from './replayDisplay';
import {
  inkOn,
  motionWords,
  multiBounds,
  multiEventFeatures,
  multiTrackFeatures,
  truckCasing,
  truckColor,
  type Basemap,
  type MultiTruck,
} from './replayMulti';

const DEFAULT_VIEW = { longitude: 28.5, latitude: -26.0, zoom: 6 };
const PUCK_PX = 34;
// In-map marks keep literal colours: the basemaps do not follow the app theme.
const MARK_INK = '#1e293b';
const MARK_PAPER = '#ffffff';

const mapButton =
  'rounded-lg shadow-lg px-3 py-1.5 text-xs font-medium backdrop-blur transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
const mapButtonOff = 'bg-surface/95 text-slate-700 hover:bg-surface hover:text-slate-900';
const mapButtonOn = 'bg-primary-700 text-surface hover:bg-primary-800';

const BASEMAPS: { id: Basemap; label: string }[] = [
  { id: 'street', label: 'Street' },
  { id: 'satellite', label: 'Satellite' },
];

export interface ReplayMultiMapProps {
  trucks: MultiTruck[];
  /** Truck or loader silhouette per truck key. */
  kinds: ReadonlyMap<string, AssetMarkerKind>;
  /** Each truck at the replay clock (null: no positions in the window). */
  positions: ReadonlyMap<string, ReplayMapPosition | null>;
  /** Each truck's display at the replay clock (the organisation's rules in force then). */
  displays: ReadonlyMap<string, AssetDisplay | null>;
  /** Trucks whose track is switched off (the puck stays). */
  hidden: ReadonlySet<string>;
  basemap: Basemap;
  onBasemapChange: (b: Basemap) => void;
  /** The truck whose card is open. */
  openKey: string | null;
  onOpenChange: (key: string | null) => void;
  /** The replay window (the zones in force over it). */
  window: { start: number; end: number };
  cursor: number | null;
  /** A new value fits the camera to every track. */
  fitKey: string;
  /** Asked from the list: centre on this truck (`n` changes on every ask). */
  focus?: { key: string; n: number } | null;
  /** The single-truck replay of a truck over the same window. */
  aloneHref: (key: string) => string;
  /** The status keys the trucks show over the replay (the legend's puck colours). */
  statusKeys: AssetStatusKey[];
  displayLegend?: ReplayDisplayLegend | null;
  className?: string;
}

interface TruckPuckProps {
  truckKey: string;
  label: string;
  /** The truck's own colour (its tag). */
  color: string;
  kind: AssetMarkerKind;
  status: AssetStatusKey;
  heading: number | null;
  motion: string;
  hollow: boolean;
  overlays: AssetDisplay['overlays'] | undefined;
  isOpen: boolean;
  onToggle: (key: string) => void;
  buttonRef: (key: string, el: HTMLButtonElement | null) => void;
}

/**
 * One truck's puck and tag. Memoised on what it shows: the clock moves the Marker around it (its
 * position) on every frame, but the face is only re-drawn when the status, the heading (whole
 * degrees), the motion or the marks change — 25 pucks at 15 frames a second (measured, ST-00277).
 */
const TruckPuck = memo(function TruckPuck({
  truckKey,
  label,
  color,
  kind,
  status,
  heading,
  motion,
  hollow,
  overlays,
  isOpen,
  onToggle,
  buttonRef,
}: TruckPuckProps) {
  return (
    <button
      ref={(el) => buttonRef(truckKey, el)}
      type="button"
      title={`${label}: show its status and speed`}
      aria-label={`${label}: show its status and speed at the replay time`}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      onClick={(ev) => {
        ev.stopPropagation();
        onToggle(truckKey);
      }}
      data-testid="replay-multi-asset"
      data-truck={truckKey}
      data-motion={motion}
      data-status={status}
      className="relative block p-0 border-0 bg-transparent cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      style={{ width: PUCK_PX, height: PUCK_PX }}
    >
      <MarkerHalo overlays={overlays} size={PUCK_PX}>
        <AssetMarkerIcon
          kind={kind}
          status={status}
          heading={heading}
          moving={motion === 'moving'}
          hollow={hollow}
          size={PUCK_PX}
        />
      </MarkerHalo>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 -mt-0.5 px-1.5 py-px rounded text-[10px] font-semibold leading-tight whitespace-nowrap shadow"
        style={{ background: color, color: inkOn(color) }}
      >
        {label}
      </span>
    </button>
  );
});

export default function ReplayMultiMap({
  trucks,
  kinds,
  positions,
  displays,
  hidden,
  basemap,
  onBasemapChange,
  openKey,
  onOpenChange,
  window: win,
  cursor,
  fitKey,
  focus = null,
  aloneHref,
  statusKeys,
  displayLegend = null,
  className = 'relative z-0 h-[380px] lg:h-[520px] rounded-xl overflow-hidden border border-slate-200',
}: ReplayMultiMapProps) {
  const mapRef = useRef<MaplibreMap | null>(null);
  const zones = useReplayZones(win, cursor); // ST-00272, unchanged
  const tracks = useMemo(() => multiTrackFeatures(trucks, hidden, basemap), [trucks, hidden, basemap]);
  const events = useMemo(() => multiEventFeatures(trucks, hidden), [trucks, hidden]);
  const bounds = useMemo(() => multiBounds(trucks), [trucks]);

  const fit = useCallback(() => {
    const map = mapRef.current;
    if (!map || !bounds) return;
    try {
      map.fitBounds(bounds as LngLatBoundsLike, { padding: 56, duration: 0, maxZoom: 15 });
    } catch {
      /* map not ready */
    }
  }, [bounds]);

  const fitted = useRef<string | null>(null);
  useEffect(() => {
    if (!bounds || fitted.current === fitKey) return;
    fitted.current = fitKey;
    fit();
  }, [fitKey, bounds, fit]);

  // Centre on a truck when the list asks (not on every frame: only when `n` changes).
  const focusN = focus?.n;
  useEffect(() => {
    if (!focus) return;
    const p = positions.get(focus.key);
    const map = mapRef.current;
    if (!p || !map) return;
    try {
      map.easeTo({ center: [p.lon, p.lat], duration: 500, zoom: Math.max(map.getZoom(), 11) });
    } catch {
      /* map not ready */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusN]);

  const onLoad = (e: { target: MaplibreMap }) => {
    mapRef.current = e.target;
    fitted.current = fitKey;
    fit();
  };

  // ST-00270: Esc closes the card and returns focus to its truck.
  const infoTitleId = useId();
  const buttons = useRef(new Map<string, HTMLButtonElement | null>());
  const buttonRef = useCallback((key: string, el: HTMLButtonElement | null) => {
    buttons.current.set(key, el);
  }, []);
  const openRef = useRef(openKey);
  useEffect(() => {
    openRef.current = openKey;
  }, [openKey]);
  const toggleOpen = useCallback(
    (key: string) => onOpenChange(openRef.current === key ? null : key),
    [onOpenChange]
  );
  const closeInfo = useCallback(
    (refocus: boolean) => {
      const key = openKey;
      onOpenChange(null);
      if (refocus && key) buttons.current.get(key)?.focus();
    },
    [openKey, onOpenChange]
  );
  useEffect(() => {
    if (!openKey) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeInfo(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openKey, closeInfo]);

  const toggleButton = (label: string, on: boolean, toggle: () => void) => (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      className={`${mapButton} ${on ? mapButtonOn : mapButtonOff}`}
    >
      {label}
    </button>
  );

  const open = openKey ? trucks.find((t) => t.key === openKey) || null : null;
  const openPos = open ? positions.get(open.key) || null : null;
  const openDisplay = open ? displays.get(open.key) || null : null;
  const openHollow =
    !!openPos && (openPos.motion === 'before' || openPos.motion === 'after' || (!!openDisplay && isHollowDisplay(openDisplay)));

  const ownRules = (displayLegend?.entries || []).filter((e) => e.kind === 'rule');
  const marks = (displayLegend?.entries || []).filter((e) => e.kind === 'overlay' && e.mark && e.mark !== 'dashed');
  const legendItem = (key: string, swatch: ReactNode, label: string) => (
    <li key={key} className="inline-flex items-center gap-1.5">
      {swatch}
      {label}
    </li>
  );

  return (
    <div>
      <div className={className}>
        <LexiStarMap
          className="absolute inset-0"
          initialViewState={DEFAULT_VIEW}
          basemap={basemap}
          onBasemapChange={onBasemapChange}
          showBasemapToggle={false}
          navigationPosition="top-left"
          onLoad={onLoad}
          overlay={
            <>
              <div className="absolute top-3 right-3 z-[1000] flex gap-2">
                {zones.available && toggleButton('Zones', zones.on, zones.toggle)}
                <button type="button" onClick={fit} className={`${mapButton} ${mapButtonOff}`}>
                  Fit
                </button>
              </div>
              <div
                className="absolute bottom-12 sm:bottom-3 left-3 z-[1000] flex overflow-hidden rounded-lg shadow-lg bg-surface/95 backdrop-blur text-xs font-medium"
                role="group"
                aria-label="Basemap"
              >
                {BASEMAPS.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => onBasemapChange(b.id)}
                    aria-pressed={basemap === b.id}
                    className={`px-3 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 ${
                      basemap === b.id ? mapButtonOn : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </>
          }
        >
          {zones.layer}
          {tracks.features.length > 0 && (
            <Source id="rp-multi-tracks" type="geojson" data={tracks}>
              <Layer
                id="rp-multi-casing"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': truckCasing(basemap), 'line-width': 5.5, 'line-opacity': 0.8 }}
              />
              <Layer
                id="rp-multi-line"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': ['get', 'color'], 'line-width': 3.5 }}
              />
            </Source>
          )}
          {events.features.length > 0 && (
            <Source id="rp-multi-events" type="geojson" data={events}>
              <Layer
                id="rp-multi-event-circle"
                type="circle"
                paint={{
                  'circle-radius': 3.5,
                  'circle-color': MARK_PAPER,
                  'circle-stroke-color': MARK_INK,
                  'circle-stroke-width': 1.5,
                }}
              />
            </Source>
          )}
          {trucks.map((t) => {
            const p = positions.get(t.key);
            if (!p) return null;
            const d = displays.get(t.key) || null;
            return (
              <Marker key={t.key} longitude={p.lon} latitude={p.lat} style={{ zIndex: openKey === t.key ? 4 : 3 }}>
                <TruckPuck
                  truckKey={t.key}
                  label={t.label}
                  color={truckColor(t.index, basemap)}
                  kind={kinds.get(t.key) || 'truck'}
                  status={(d?.base.token as AssetStatusKey | undefined) ?? stageKey(p.stage)}
                  // Whole degrees: the face is re-drawn when the heading turns, not on every frame.
                  heading={p.heading === null ? null : Math.round(p.heading)}
                  motion={p.motion}
                  hollow={p.motion === 'before' || p.motion === 'after' || (!!d && isHollowDisplay(d))}
                  overlays={d?.overlays}
                  isOpen={openKey === t.key}
                  onToggle={toggleOpen}
                  buttonRef={buttonRef}
                />
              </Marker>
            );
          })}
          {open && openPos && (
            <Popup
              longitude={openPos.lon}
              latitude={openPos.lat}
              // No fixed anchor: the card opens on the side with room (a truck near the map's
              // edge would otherwise push it out of view).
              offset={PUCK_PX / 2 + 2}
              // Above every puck (they sit at z-index 3–4): the card is never covered by a truck.
              style={{ zIndex: 6 }}
              closeOnClick
              onClose={() => closeInfo(false)}
              className={ASSET_INFO_POPUP_CLASS}
              maxWidth="280px"
            >
              <div role="dialog" aria-labelledby={infoTitleId} data-testid="replay-multi-asset-info">
                <AssetInfoCard
                  titleId={infoTitleId}
                  name={open.label}
                  subtitle={open.fleetNo || ((kinds.get(open.key) || 'truck') === 'loader' ? 'Loader' : 'Truck')}
                  statusKey={(openDisplay?.base.token as AssetStatusKey | undefined) ?? stageKey(openPos.stage)}
                  statusLabel={openDisplay?.base.label}
                  motionText={motionWords(openPos)}
                  speedKmh={openHollow ? null : openPos.speed_kmh}
                  when={`At ${clockTime(openPos.t)}`}
                >
                  <DisplayMarks overlays={openDisplay?.overlays} className="mt-1.5" />
                  <Link
                    to={aloneHref(open.key)}
                    className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-700 hover:underline"
                  >
                    Replay this truck alone <FaExternalLinkAlt size={9} aria-hidden="true" />
                  </Link>
                </AssetInfoCard>
              </div>
            </Popup>
          )}
        </LexiStarMap>
      </div>
      <ul
        className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 px-1 text-[11px] text-slate-600"
        aria-label="Map legend"
      >
        {legendItem(
          'track',
          <span className="inline-flex gap-0.5" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span key={i} className="inline-block w-2 h-1 rounded" style={{ background: truckColor(i, basemap) }} />
            ))}
          </span>,
          'Track: one colour per truck'
        )}
        {legendItem(
          'puck',
          <AssetMarkerIcon kind="truck" status="loaded" heading={90} size={18} style={{ filter: 'none' }} />,
          'Truck at the replay time, in its status colour:'
        )}
        {statusKeys.map((k) =>
          legendItem(
            `status-${k}`,
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${ASSET_STATUS_CLASSES[k].dot}`} data-legend-key={k} />,
            ASSET_STATUS[k].label
          )
        )}
        {ownRules.map((e) =>
          legendItem(
            `rule-${e.id}`,
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${ASSET_STATUS_CLASSES[e.token as AssetStatusKey].dot}`}
              data-legend-rule={e.id}
            />,
            e.label
          )
        )}
        {marks.map((e) =>
          legendItem(
            `mark-${e.id}`,
            <span className="inline-flex w-5 justify-center" data-legend-mark={e.id}>
              <MarkGlyph mark={e.mark!} token={e.token} />
            </span>,
            e.label
          )
        )}
        {legendItem(
          'event',
          <span
            className="inline-block w-2 h-2 rounded-full border-2"
            style={{ borderColor: MARK_INK, background: MARK_PAPER }}
          />,
          'Event'
        )}
        {zones.legend}
      </ul>
      {displayLegend && displayLegend.notShown.length > 0 && (
        <p className="mt-1 px-1 text-[11px] text-slate-500" data-testid="replay-not-historical">
          Your map colours also use {displayLegend.notShown.join(', ')}. Replay does not keep those for
          past moments, so the rules that need them cannot show here.
        </p>
      )}
    </div>
  );
}
